import { ProviderData } from "@/types/provider";
import { 
  Wallet, 
  Clock, 
  TrendingUp, 
  CheckCircle2, 
  Building2
} from "lucide-react";
import samblaLogo from "@/assets/sambla_stor.png";
import ucImage from "@/assets/UC-3.png";
import bankTransferIcon from "@/assets/banköverföring.png";

export const samblaData: ProviderData = {
  id: "sambla",
  name: "Sambla",
  slug: "sambla",
  logo: samblaLogo,
  type: "comparison",
  numberOfLenders: 40,
  
  trustpilot: {
    rating: 4.8,
    reviews: 29456,
    url: "https://se.trustpilot.com/review/sambla.se"
  },
  
  hero: {
    badges: [
      { text: "Jämför 40 långivare med en ansökan", variant: "default" }
    ],
    description: "Sambla är en låneförmedlare som hjälper dig att jämföra privatlån, billån och bolån. Hos Sambla kan du även samla dina befintliga lån för att försöka minska dina räntekostnader och förbättra ditt kreditbetyg. Du kan ansöka om lån upp till 800 000 kronor, och med en enda ansökan kan du få erbjudanden från en eller flera av Samblas cirka 40 samarbetspartners.",
    ctaText: "Jämför lån hos Sambla",
    ctaButtonText: "Ansök hos Sambla",
    ctaButtonVariant: "default"
  },
  
  stats: [
    {
      icon: Building2,
      value: "40+",
      label: "Långivare",
      description: "Jämför många alternativ"
    },
    {
      icon: Wallet,
      value: "800 000 kr",
      label: "Maxbelopp",
      description: "Från 5 000 kr"
    },
    {
      icon: Clock,
      value: "Svar inom 24h",
      label: "Smidig process",
      description: "Snabb hantering"
    },
    {
      icon: TrendingUp,
      value: "5,08 - 32,99%",
      label: "Effektiv ränta",
      description: "Individuell prissättning"
    },
    {
      image: ucImage,
      value: "UC",
      label: "Kreditupplysning",
      description: "Påverkar UC-Score"
    },
    {
      icon: CheckCircle2,
      value: "Ja",
      label: "Betalningsanmärkningar",
      description: "Hos vissa långivare"
    }
  ],
  
  keyInformation: [
    {
      image: ucImage,
      title: "Kreditupplysning",
      value: "UC",
      description: "Endast en UC-förfrågan trots flera erbjudanden"
    },
    {
      image: bankTransferIcon,
      title: "Utbetalning via",
      value: "Banköverföring",
      description: "1-4 bankdagar efter signering"
    },
    {
      icon: CheckCircle2,
      title: "Effektiv ränta",
      value: "5,08 – 32,99 %",
      description: "Varierar mellan långivarna"
    },
    {
      icon: Building2,
      title: "Samarbetspartner",
      value: "40",
      description: "Anslutna långivare"
    }
  ],
  
  termsAndConditions: [
    { term: "Lånebelopp", value: "5 000 – 800 000 kr" },
    { term: "Nominell ränta", value: "4,50 – 22,00 %" },
    { term: "Effektiv ränta", value: "5,08 – 32,99 %" },
    { term: "Löptid", value: "1 – 20 år" },
    { term: "Uppläggningsavgift", value: "Varierar mellan långivare" },
    { term: "Lån utan UC", value: "Nej" },
    { term: "Svar på ansökan", value: "Samma dag" },
    { term: "Betalningslösning", value: "Banköverföring" }
  ],
  
  loanRequirements: [
    { requirement: "Legitimering", value: "Nej" },
    { requirement: "Folkbokförd i Sverige", value: "Ja, minst 1 år" },
    { requirement: "Svenskt personnummer", value: "Ja" },
    { requirement: "Svenskt mobilnummer", value: "Ja" },
    { requirement: "Svenskt kontonummer", value: "Nej" },
    { requirement: "Minsta årsinkomst", value: "100 800 kr" },
    { requirement: "Bet.anmärkning OK?", value: "Ja" },
    { requirement: "Ålderskrav", value: "18 +" }
  ],
  
  features: {
    pros: [
      "Jämför många olika banker och långivare",
      "Generösa öppettider",
      "Omtyckt av kunderna"
    ],
    cons: [
      "Inget samarbete med storbanker"
    ]
  },
  
  interestRateSection: {
    title: "Samblas ränta",
    description: "Att ansöka om lån via Sambla är kostnadsfritt. Räntan och månadskostnaden på ditt lån bestäms inte av Sambla utan av deras samarbetspartners.\n\nNär du gjort en låneansökan via Sambla bedömer långivarna din kreditvärdighet utifrån den kreditupplysning som görs hos UC, och lånevillkoren blir sedan individuellt anpassade. De tittar till exempel på din betalningsförmåga och kreditrisk.\n\nHär ser du en sammanställning av Samblas avgifter och kostnader. Observera att lånemarknaden är föränderlig och villkoren kan uppdateras med kort varsel. Vi rekommenderar därför alltid att du noggrant läser igenom de aktuella lånevillkoren på Samblas egna hemsida innan du ansöker om lån för att få den mest uppdaterade informationen.",
    rateTable: [
      { label: "Lånebelopp (lägsta – högsta)", value: "5 000 – 800 000 kr" },
      { label: "Nominell ränta", value: "4,50 – 22,00 %" },
      { label: "Effektiv ränta", value: "5,08 – 32,99 %" },
      { label: "Uppläggningsavgift", value: "Varierar mellan långivare" },
      { label: "Fakturaavgift (Pappersfaktura)", value: "Varierar mellan långivare" },
      { label: "Fakturaavgift (Autogiro)", value: "Varierar mellan långivare" }
    ]
  },
  
  payoutSection: {
    title: "Samblas utbetalning",
    description: "När du har signerat avtalet betalar långivaren ut lånet. Hur lång utbetalningstiden är kan variera något beroende på vilken långivare du väljer och vilken bank du har, men oftast har du pengarna inom 2–3 bankdagar.",
    payoutTable: [
      { timeframe: "Beviljad före 12:00", duration: "1-4 dagar" },
      { timeframe: "Beviljad efter 12:00", duration: "1-4 dagar" }
    ],
    disclaimer: "Utbetalningar sker endast under bankdagar. Om du ansöker eller får godkännande på en helg eller helgdag kan det dröja längre innan pengarna finns tillgängliga på ditt konto."
  },
  
  aboutSections: [
    {
      title: "Om Sambla",
      content: [
        "Sambla är en låneförmedlare som hjälper dig att jämföra och hitta det bästa privatlånet för dina behov. Genom en enda ansökan kan du få upp till 40 olika låneerbjudanden från olika långivare, vilket ger dig möjligheten att välja det lån som passar dig bäst – både när det gäller ränta, återbetalningstid och övriga villkor.",
        "Sambla grundades 2015 och har sedan dess hjälpt tusentals svenskar att hitta rätt lån. Företaget erbjuder inte bara privatlån utan även jämförelse för billån, bolån och samlingslån, vilket gör dem till en komplett plattform för olika lånebehov."
      ]
    },
    {
      title: "Långivare hos Sambla",
      content: [
        "Sambla samarbetar med 40 olika långivare, vilket ger dig som låntagare en bred palett av alternativ. Även om de stora storbankerna som Nordea, SEB, Swedbank och Handelsbanken inte finns med bland Samblas samarbetspartners, samarbetar de med många andra etablerade och konkurrenskraftiga långivare.",
        "Att Sambla har så många samarbetspartners innebär att du har större chans att hitta ett lån som passar just dina förutsättningar, oavsett om du har perfekt kreditvärdighet eller betalningsanmärkningar."
      ]
    },
    {
      title: "Hur fungerar Sambla?",
      content: [
        "Att ansöka om lån via Sambla är enkelt och smidigt. Du börjar med att fylla i en ansökan direkt på Samblas hemsida där du anger önskat lånebelopp och återbetalningstid. Efter att du skickat in ansökan gör Sambla en kreditupplysning hos UC och skickar din ansökan till relevanta långivare som de samarbetar med.",
        "Du får sedan låneerbjudanden från flera långivare som du kan jämföra i lugn och ro. Det bästa är att det bara blir en UC-förfrågan, trots att flera långivare bedömer din ansökan. När du har valt det låneerbjudande som passar dig bäst signerar du avtalet digitalt med BankID, och pengarna betalas ut inom några arbetsdagar.",
        "Sambla erbjuder även möjlighet att ansöka med en medsökande, vilket kan öka dina chanser att få bättre lånevillkor om ni tillsammans har starkare ekonomi."
      ]
    }
  ],
  
  faq: [
    {
      question: "Accepterar långivarna hos Sambla betalningsanmärkningar?",
      answer: "Ja, ett fåtal långivare hos Sambla kan acceptera betalningsanmärkningar om de bedömer att du har bra nuvarande betalningsförmåga. Möjligheten att bli godkänd varierar mellan olika långivare och beror på din individuella ekonomiska situation."
    },
    {
      question: "Kan man låna utan UC via Sambla?",
      answer: "Nej, Sambla använder UC för kreditupplysningar, vilket påverkar din UC-Score. Om du vill undvika detta kan du istället se vår jämförelse av lån utan UC."
    },
    {
      question: "När betalas lånet ut?",
      answer: "Utbetalningstiden hos de långivare som Sambla samarbetar med är vanligtvis 1 till 3 bankdagar. Tidpunkten för utbetalningen beror på vilken långivare som godkänner ditt lån och när ansökan blir godkänd. Utbetalningar sker endast under bankdagar."
    },
    {
      question: "Erbjuder långivarna hos Sambla utbetalning på helger?",
      answer: "Långivarna hos Sambla gör inte utbetalningar på helger. När du begär en utbetalning får du normalt pengarna nästa bankdag."
    },
    {
      question: "Kostar det något att använda Sambla?",
      answer: "Nej, Sambla är kostnadsfritt för dig som använder tjänsten. Däremot får de en ersättning från långivaren."
    },
    {
      question: "Vilka typer av lån jämför Sambla?",
      answer: "Sambla jämför lån utan säkerhet och bolån. Du kan även ansöka om och jämföra samlingslån hos Sambla."
    },
    {
      question: "Hur funkar Sambla?",
      answer: "Du ansöker om lån hos Sambla direkt på deras hemsida och enligt uppgift ska du få svar inom 24 timmar. Sammanlagt samarbetar Sambla med 40 olika långivare, men något som kan vara bra att veta är att långivarna som Sambla samarbetar med inte måste erbjuda dig ett lån. Det är alltså inte garanterat att du får svar från alla banker som Sambla kontaktar. Sambla fungerar som mellanhand i kontakten mellan dig och långivarna."
    },
    {
      question: "Kan jag bli beviljad lån trots betalningsanmärkningar?",
      answer: "Ja, Sambla samarbetar med flera långivare som godkänner att du har upp till 10 betalningsanmärkningar. Däremot krävs det en månadslön på minst 8400 kronor och att du har varit skuldfri hos Kronofogden sedan 6 månader. De banker och långivare vi samarbetar med gör alltid en individuell bedömning på hela din ekonomiska situation vid ansökan, oavsett antal betalningsanmärkningar."
    },
    {
      question: "Är en ansökan bindande hos Sambla?",
      answer: "Efter du skickat ansökan får du svar från flera banker. Därefter väljer du vilket låneförslag du vill ha hemskickat. Först när du signerat låneförslaget med bläckpenna eller Bank-ID är lånet bindande. Glöm inte att du kostnadsfritt kan förtidsinlösa lån om du skulle ändra dig."
    },
    {
      question: "Görs det endast en kreditupplysning?",
      answer: "Ja, när du söker privatlån genom oss görs bara en kreditupplysning hos UC, trots att du får upp till 40 olika låneerbjudanden. Däremot måste alla långivare som tagit del av dina uppgifter skicka en separat omfrågandekopia. I enstaka fall kan vissa banker ta in ytterligare information som tillägg till informationen från UC för att förbättra kvaliteten i kreditbeslutet. För att maximera chansen till bra lånevillkor rekommenderar vi dig även att inkludera en medsökande i din jämförelse. Detta är ofta en stor fördel när bankerna gör sin bedömning."
    }
  ],
  
  customerService: {
    description: "Samblas kundtjänst finns tillgänglig för att hjälpa dig med frågor om din låneansökan och jämförelsetjänsten. Du kan nå dem via telefon eller e-post.",
    phone: "0770-220180",
    phoneHours: [
      "Vardagar: 09.00-20.30",
      "Lördag: 12.00-16.00",
      "Söndag: 12.00-16.00"
    ],
    email: "kundtjanst@sambla.se",
    emailHours: ["Svarar inom 24 timmar"]
  },
  
  companyInfo: {
    name: "Sambla Group AB",
    orgNumber: "556974-8378",
    address: "Strandvägen 5 B",
    city: "114 51 Stockholm",
    website: "https://www.sambla.se",
    fiRegisterUrl: "https://www.fi.se/sv/vara-register/foretagsregistret/details?id=127540"
  },
  
  affiliateLink: {
    linkId: "sambla-affiliate-link-id",
    trackingSource: "sambla_page",
    url: "https://www.sambla.se/lana-pengar/"
  },
  
  seo: {
    title: "Sambla - Jämför privatlån från 40 långivare | KlarFinans",
    description: "Ansök om privatlån hos Sambla och jämför erbjudanden från 40 olika långivare. Belopp 5 000 - 800 000 kr, svar samma dag. Även billån, bolån och samlingslån.",
    keywords: [
      "sambla",
      "privatlån",
      "jämför lån",
      "låneförmedlare",
      "40 långivare",
      "samlingslån",
      "billån",
      "bolån"
    ]
  },
  
  schema: {
    productName: "Sambla Privatlån - Jämför 40 långivare",
    description: "Jämför privatlån från 40 olika långivare med en ansökan. Belopp 5 000 - 800 000 kr. Svar inom 1 dygn. Erbjuder även billån, bolån och samlingslån.",
    url: "https://klarfinans.se/sv-SE/privatlan/sambla",
    provider: "Sambla",
    interestRate: {
      min: 5.08,
      max: 32.99
    },
    minAmount: 5000,
    maxAmount: 800000,
    loanTerm: ["1 år", "2 år", "3 år", "5 år", "10 år", "15 år", "20 år"],
    featureList: [
      "Jämför lån från 40 olika långivare med en ansökan",
      "Lån från 5 000 kr upp till 800 000 kr",
      "Svar inom 1 dygn (samma dag)",
      "Flexibla återbetalningsvillkor 1-20 år",
      "Effektiv ränta 5,08 – 32,99 %",
      "Kan acceptera betalningsanmärkningar hos vissa långivare",
      "Erbjuder även billån, bolån och samlingslån",
      "Endast en UC-kreditupplysning trots flera erbjudanden"
    ]
  }
};
